import { computed, reactive, ref, watch } from 'vue';
import config from '@/config/simulation';
import { getCO2Tax } from '@/config';
import { addVAT, formatCurrency } from '@/helpers/utils';
import { useRouter } from 'vue-router';

const simulationDefaults = {
  zipCode: null,
  consumption: null,
  power: null,
  fuelConsumption: null,
  woodConsumption: null,
  consumptionKnown: null,
  location: null,
  cityIndex: null,
  energy: null,
  buildingUsage: '',
  buildingAltitude: null,
  buildingDate: null,
  buildingType: null,
  mobilityConsumption: 4.76,
  distance: 10370,
};

const usageId = ref(null);
const product = ref(null);
const simulation = reactive({ ...simulationDefaults });
const formPluginElement = ref(null);

const resetSimulation = () => {
  Object.assign(simulation, simulationDefaults);
};

const resetProduct = () => {
  product.value = null;
};

const updateUsageId = (data) => {
  usageId.value = data;
};

const updateSimulation = (data) => {
  Object.assign(simulation, { ...data });
};

const usage = computed(() => {
  return config.usages[usageId.value];
});

const city = computed(() => {
  if (simulation.cityIndex !== null) {
    return simulation.location?.citiesAvailable?.[simulation.cityIndex];
  }
  return null;
});

// Assign the computed city to reactive object simulation.city
simulation.city = city;

const questions = computed(() => {
  return usage.value?.questions;
});

const estimationCompleted = computed(() => {
  switch (usageId.value) {
    case 'cooking':
      return !!simulation.consumption;
    case 'heating':
    case 'industrial':
      return !!simulation.consumption && !!simulation.power;
    case 'mobility':
      return !!simulation.mobilityConsumption && !!simulation.distance;
  }
  return false;
});

const rateGroup = computed(() => {
  if (usageId.value === 'cooking') {
    return 'cooking';
  } else if (usageId.value === 'heating') {
    return 'comfort';
  }
  return 'other';
});

const mobilityTotal = computed(() => {
  return (simulation.distance / 100) * simulation.mobilityConsumption;
});

const shouldIncludePower = computed(() => {
  if (usageId.value === 'cooking') return false;
  if (rateGroup.value === 'other' && simulation.consumption < 25000) {
    const hours = simulation.consumption / simulation.power;
    if (hours < 1000) {
      return false;
    }
  }
  return true;
});

const costs = computed(() => {
  const consumption = simulation.consumption;
  const power = simulation.power;
  const rate = config.rates[rateGroup.value].find(
    (rate) => consumption >= rate.min
  );
  const prices = {};

  Object.keys(rate.prices).map((percentage) => {
    let price = rate.prices[percentage];
    price += getCO2Tax() * ((100 - percentage) / 100);
    let total = consumption * price;

    if (shouldIncludePower.value) {
      total += power * rate.power;
    }

    total += rate.subscription;
    total = addVAT(total);

    prices[percentage] = formatCurrency(total);
  });

  return {
    prices,
    subscription: rate.subscription,
  };
});

const consumptionField = computed(() => {
  return {
    label: gettext('Consommation annuelle'),
    name: 'consumption',
    required: true,
  };
});

watch(usageId, (val, oldVal) => {
  // Reset all questions’ answers on usage change
  if (oldVal === null) return;
  resetSimulation();
});

watch(
  () => simulation.location?.isAvailable,
  (val, oldVal) => {
    if (oldVal === null) return;
    updateSimulation({
      consumption: null,
      power: null,
      buildingAltitude: null,
    });
  }
);

watch(
  () => simulation.energy,
  (val, oldVal) => {
    // In case the user doesn’t know its consumption, there’s no need to reset
    // the calculation as the values will be the same whatever the energy chosen
    if (oldVal === null || simulation.consumptionKnown === false) return;
    simulation.consumption = null;
    simulation.power = null;
  }
);

watch(
  () => simulation.consumptionKnown,
  (val, oldVal) => {
    if (oldVal === null) return;

    // Reset all the building data otherwise when the user comes back to this mode
    // all questions will be answered but the estimation won’t be displayed
    updateSimulation({
      consumption: null,
      power: null,
      buildingDate: null,
    });
  }
);

watch(
  () => simulation.buildingAltitude,
  (val, oldVal) => {
    if (oldVal === null) return;

    // Reset the building type because the list depends on buildingUsage answer
    // User might have previously picked an answer that is not in the list anymore
    updateSimulation({
      buildingType: null,
      consumption: null,
      power: null,
    });
  }
);

/**
 * Watch some specific "estimation" questions to compute consumption & power
 */
watch(
  // Trigger calculation only if one of these values change
  () => {
    return (
      simulation.buildingUsage +
      simulation.buildingAltitude +
      simulation.buildingDate +
      simulation.buildingType +
      simulation.city?.cityHeight
    );
  },
  () => {
    if (
      !simulation.buildingType ||
      !simulation.buildingDate ||
      !simulation.buildingUsage ||
      !(simulation.buildingAltitude || simulation.city)
    ) {
      return;
    }

    // If there’s no altitude at this point, it means we didn't ask the customer for it
    // We can determine it from the city height since this location is serviced
    if (!simulation.buildingAltitude) {
      simulation.buildingAltitude =
        simulation.city?.cityHeight > 1000 ? 'high' : 'low';
    }

    const { consumption, power } =
      config.buildings[simulation.buildingType][simulation.buildingDate][
        simulation.buildingAltitude
      ][simulation.buildingUsage];

    simulation.consumption = consumption;
    simulation.power = power;

    updateSimulation({
      consumption,
      power,
    });
  }
);

export function useSimulation() {
  const router = useRouter();

  const order = (p) => {
    product.value = p;
    router.push({ name: 'order' });
  };

  return {
    usageId,
    updateUsageId,
    usage,
    questions,
    simulation,
    city,
    updateSimulation,
    estimationCompleted,
    product,
    resetProduct,
    mobilityTotal,
    shouldIncludePower,
    costs,
    rateGroup,
    formPluginElement,
    consumptionField,
    order,
  };
}
